<template>
    <div class="content-widget">
        <Loader
            v-if="isLoadingCutaway"
            :messageType="'loading-cutaway-view'"
        />
        <template v-else>
            <CutawayHeader
                v-if="!isAlternativeMode && data.floors"
                :floor="floorText"
                :widget="'cutaway'"
                :isOpenedSidebar="isOpenedSidebar"
                :plotCount="plotCount"
            />
            <EditKrpano
                v-if="canBeEditable && data"
                :data="data"
                :krpanoHotspot="data.krpanoHotspot"
                :sceneKey="data.sceneKey"
                :type="data.type"
                :widgetTitle="data.widgetTitle"
                :editProperties="editProperties"
                :defaultProperties="defaultProperties"
                :loaded="!isLoadingCutaway"
                :updateJsonUrl="updateJsonUrl"
                :link="link"
                :active="active"
                type="cutawayModel"
                class="editkrpano"
            />
            <ScrollButton
                v-if="isWheelLocked"
                :toggleModeWheel="toggleModeWheel"
                :options="data"
            />
            <PopupInformation
                :data="popup"
                :update="newState"
                :target="targetElement"
                :popupType="'plot'"
            />
            <SearchPopup
                v-if="isDisplayedSearchDialog"
                :widget="'cutaway'"
                :isOpenedSidebar="isOpenedSidebar"
                :lotView="data.lotView"
                :hasAlternativePlot="hasVisiblePlots"
                @close-dialog="closeDialog"
            />
        </template>

        <ThreeDController
            v-if="data.gui && data.gui.type === 'cutaway'"
            :options="data"
            :widget="widget"
            :targetElement="targetElement"
            :useCompass="useCompass"
            :widgetType="'cutaway'"
        />
        <div
            ref="cutawayHolder"
            class="content-widget"
            @click="antiScroll()"
        />
    </div>
</template>

<script>
    import { EventBus } from '@/event-bus';
    import Http from '@/api/data';
    import { mapActions, mapGetters } from 'vuex';

    import ThreeDController from "@/components/threeDController/ThreeDController";
    import ScrollButton from "@/components/ScrollButton";
    import PopupInformation from '@/components/plotInformation/PopupInformation';
    import Loader from "@/components/Loader";
    import CutawayHeader from "@/components/cutawayHeader/CutawayHeader";
    import SearchPopup from "@/components/searchPopup/SearchPopup";
    import EditKrpano from '@/components/editMode/EditKrpano';

    export default {
        name: 'CutawayWidget',
        components: {
            SearchPopup,
            CutawayHeader,
            ScrollButton,
            ThreeDController,
            PopupInformation,
            Loader,
            EditKrpano
        },
        props: [
            'data',
            'link',
            'active',
            'standalone',
            'isOpenedSidebar',
            'loadedWidgets'
        ],
        data () {
            return {
                isIE: false,
                targetElement: null,
                widget: null,
                popup: {
                    x: 0,
                    y: 0,
                    visible: false
                },
                toggleModeWheel: false,
                newState(item) {
                    this.updateWidget(item);
                },
                cutawayOptions: [
                    '3d',
                    'both'
                ],
                urlLink: null,
                useCompass: false,
                compass: null,
                defaultProperties: {
                    viewAngle: null,
                    zoomFactor: 1,
                    north: null
                },
                editProperties: {
                    viewAngle: null,
                    zoomFactor: 1,
                    north: null
                },
                compassData: null,
                availabilityTypeSortOrder: [
                    1,
                    3,
                    4,
                    5,
                    2,
                    100,
                    101,
                    102,
                    103,
                    104,
                    105,
                    106,
                    107,
                    108
                ],
                isEditable: false,
                firstScale: null,
                floors: null,
                currentAreaUnit: null,
                squareMeterValue: .093,
                redirectPlotGrid: false,
                isLoadingCutaway: false,
                userChoiceClosedSearchPopup: false,
                enableAvailabilities: [
                    1,      // Available
                    3       // Optionned
                ],
                compassWidget: null,
                widgetLoaded: false,
            };
        },
        computed: {
            ...mapGetters([
                'isAlternativeMode',
                'isOfflineMode',
                'isLocalstorageAvailable',
                'isInternal',
                'isReviewUse',
                'language',
                'logos',
                'protocol'
            ]),
            ...mapGetters('widget', [
                'showPlotCount',
                'displayFavorite'
            ]),
            widgetId() {
                return this.urlLink ? new URL(this.urlLink).searchParams.get('id') : new URL(this.$props.loadedWidgets.find(widget => widget.active).link).searchParams.get('id');
            },
            canBeEditable() {
                if (this.standalone && this.isEditable)
                    return true;
                return false;
            },
            isWheelLocked() {
                return window.self !== window.top;
            },
            unicPlotList() {
                let plotList = [];
                let nameList = [];
                this.getHotspotList().filter(el => el.visible).forEach(plot => {
                    if (!nameList.includes(plot.name)) {
                        plotList.push(plot);
                        nameList.push(plot.name);
                    }
                });
                if (plotList.length > 0)
                    this.userChoiceClosedSearchPopup = false;
                return plotList;
            },
            allAvailablePlot() {
                let plotList = [];
                if (this.$props.data && this.$props.data.floors)
                    this.$props.data.floors.filter(floor => {
                        if (floor.hotspots.length)
                            floor.hotspots.forEach(hotspot => {
                                let isPlotAvailable = hotspot.details.find(detail => this.enableAvailabilities.includes(detail.type) && detail.id === 'availability');
                                if (isPlotAvailable)
                                    plotList.push(hotspot);
                            });
                    });
                return plotList;
            },
            hasVisiblePlots() {
                return this.allAvailablePlot ? this.allAvailablePlot.filter(plot => plot.visible).length > 0 : false;
            },
            plotCount() {
                return this.$props.data.floors ? this.unicPlotList.length : 0;
            },
            hasSharedFavlist() {
                return this.$route.query.favlist ? true : false;
            },
            favoriteKey() {
                return `favorite-${this.$route.query.id}`;
            },
            isDisplayedSearchDialog() {
                return (this.$props.data.type && this.$props.data.type !== 'plot-grid') &&
                    !this.userChoiceClosedSearchPopup &&
                    this.showPlotCount &&
                    (this.plotCount === 0 && this.allAvailablePlot.length > 0) &&
                    !this.displayFavorite;
            },
            floorIndex() {
                return this.$props.data.floors ? this.$props.data.floors.findIndex(floor => floor.active) : 0;
            },
            floorText() {
                return this.$props.data.floors ? this.$props.data.floors[this.floorIndex].text : null;
            },
            apiUrl() {
                return this.protocol + (this.isReviewUse ? process.env.API_LIST.REVIEW : process.env.API_LIST.FO);
            },
            updateJsonUrl() { 
                return this.apiUrl + '/save/' + this.data.contentItemId;;
            },
        },
        async mounted() {
            this.isIE = window.navigator.msPointerEnabled;
            this.targetElement = this.$refs.cutawayHolder;
            this.newState = this.newState.bind(this);
            this.filterWidget = this.filterWidget.bind(this);
            this.updateWidget = this.updateWidget.bind(this);
            if (this.hasSharedFavlist && this.isLocalstorageAvailable)
                localStorage.setItem(this.favoriteKey, JSON.stringify(decodeURI(this.$route.query.favlist).split(',')));

            this.render();

            if (this.isInternal)
                this.isEditable = this.$route.query.edit !== undefined;

            window.addEventListener('resize', this.updateScale);

            EventBus.$on('updateCutawayData', () => {
                this.filterWidget();
            });

            EventBus.$on('3d_FilterHotspot', () => {
                if (this.data.floors)
                    this.filterHotspot();
            });

            EventBus.$on("compassWidget", widgetType => {
                if (widgetType === 'cutaway' && this.$props.data.compass) {
                    this.compassWidget = compassInstance;
                    this.compassWidget.refresh(this.$props.data.startIndex, this.$props.data.images[0].length);
                }
            });
        },
        methods: {
            ...mapActions([
                'setIsLoading',
                'setLogo'
            ]),
            ...mapActions('widget', [
                'setPlotCount'
            ]),
            callbacks() {
                let vm = this;
                return {
                    callbacks: {
                        overCallback(hotspot, x, y) {
                            if (vm.$vuetify.breakpoint.lgAndUp)
                                vm.popup = Object.assign(hotspot, { 'x': x, 'y': y });
                        },
                        outCallback(hotspot) {
                            vm.popup = { 'x': 0, 'y': 0 };
                        },
                        clickCallback(hotspot, x, y) {
                            vm.popup = Object.assign(hotspot, { 'x': x, 'y': y });
                        },
                        widgetReadyCallback(widget){
                            EventBus.$emit('widgetReady', widget);
                        },
                        indexCallback(currentIndex, currentViews) {
                            vm.editProperties.viewAngle = currentIndex;
                            vm.currentViews = currentViews;
                            if (vm.useCompass && vm.compassWidget) {
                                vm.compassWidget.refresh(currentIndex, currentViews);
                            }
                        },
                        zoomCallback(currentScale) {
                            vm.editProperties.zoomFactor =  currentScale.scale / vm.firstScale;
                        },
                        scaleCallback(scale) {
                            vm.firstScale = scale / vm.editProperties.zoomFactor;
                        },
                        imageReadyCallback(image) {
                            vm.isLoadingCutaway = false;
                            vm.defaultProperties = Object.assign({}, vm.editProperties);
                            vm.setIsLoading(false);
                        }
                    }
                };
            },
            render() {
                if (!['cutaway', 'plot-grid'].includes(this.data.type) || _.isEmpty(this.data.floors)) {
                    let floorIndex;
                    let availableFloors = [];
                    let hotspotList = [];
                    let hotspotNameList = [];
                    let currentHotspotList;
                    this.urlLink = this.getAPIUrl();
                    this.getCutawayViewData().then(cutawayData => {
                        cutawayData = Object.assign(cutawayData, this.callbacks(), { filterWidget: this.filterWidget });
                        if (cutawayData.options && 'showResults' in cutawayData.options)
                            this.setPlotCount(cutawayData.options.showResults)

                        cutawayData.floors.forEach((floor, index) => {
                            // Get active floor
                            if (floor.active === true)
                                floorIndex = index;

                            // Get all hotspot from each floor
                            if (floor.hotspots)
                                if (hotspotList.length >= 1)
                                    hotspotList = hotspotList.concat(floor.hotspots);
                                else
                                    hotspotList = floor.hotspots;

                            // Get unique plot counter by floor
                            currentHotspotList = [];
                            floor.hotspots.forEach(hotspot => {
                                if (!hotspotNameList.includes(hotspot.name)) {
                                    currentHotspotList.push(hotspot);
                                    hotspotNameList.push(hotspot.name);
                                }
                            });

                            // Get all floor
                            availableFloors.push({
                                'id': floor.id,
                                'text': floor.text,
                                'hotspots': currentHotspotList,
                                'active': false
                            });
                        });

                        cutawayData.id = this.widgetId;
                        cutawayData.type = 'cutaway';
                        cutawayData.gui.type = 'cutaway';
                        cutawayData.widgetType = `cutaway-${this.widgetId}`;
                        cutawayData.frames = cutawayData.floors[floorIndex].frames || [];
                        cutawayData.hotspots = cutawayData.floors[floorIndex].hotspots || [];
                        cutawayData.images = this.getFloorsImages(cutawayData.floors);
                        cutawayData.startLevel = floorIndex;

                        let areaFilter = {};
                        cutawayData.areaUnit = this.getAreaUnit(hotspotList);
                        areaFilter[cutawayData.areaUnit] = this.getInterval(hotspotList, 'surface');

                        if (!cutawayData.filters)
                            cutawayData.filters = {
                                'floors': availableFloors,
                                'priceInterval': this.getInterval(hotspotList, 'price'),
                                'availabilities': this.getPlotAvailabilityList(hotspotList),
                                'tags': this.getTagList(hotspotList),
                                'typologies': this.getTypologyList(hotspotList),
                                'searchValue': null,
                                'areaInterval': this.language !== 'en' ? areaFilter : this.convertByUnit(areaFilter[cutawayData.areaUnit])
                            };

                        if (cutawayData.startIndex)
                            this.editProperties.viewAngle = parseInt(cutawayData.startIndex);

                        if (cutawayData.compass) {
                            this.useCompass = cutawayData.compass.active;
                            this.editProperties.north = parseInt(cutawayData.compass.index);
                            if (this.canBeEditable) {
                                cutawayData.compass.active = true;
                            }
                        }
                        if (cutawayData.zoomFactor)
                            this.defaultProperties.zoomFactor = cutawayData.zoomFactor;

                        if (cutawayData.language)
                            this.$i18n.locale = cutawayData.language;

                        this.editProperties = Object.assign({}, this.defaultProperties);

                        if (!this.standalone)
                            cutawayData = Object.assign(cutawayData, { widgetTitle: cutawayData.widgetTitle });

                        if (!this.logos || (cutawayData.program.logo && (this.logos && !('program' in this.logos))))
                            this.setLogo({
                                'type': 'program',
                                'src': cutawayData.program.logo
                            });

                        if (!this.logos  || (cutawayData.promoter.logo && (this.logos && !('promoter' in this.logos))))
                            this.setLogo({
                                'type': 'promoter',
                                'src': cutawayData.promoter.logo
                            });

                        if (this.hasSharedFavlist)
                            this.redirectPlotGrid = true;

                        this.loadWidget(cutawayData);
                    },
                    errorMsg => {
                        console.log(errorMsg);
                    });
                } else {
                    let floorIndex = this.$props.data.floors.findIndex(floor => floor.active);
                    let localData = _.cloneDeep(this.$props.data);

                    localData.frames = localData.floors[floorIndex].frames;
                    localData.hotspots = localData.floors[floorIndex].hotspots;
                    localData.images = this.getFloorsImages(localData.floors);
                    localData.widgetType = `cutaway-${this.widgetId}`;
                    localData.startLevel = floorIndex;
                    if (localData.language)
                        this.$i18n.locale = localData.language;
                    if (!this.standalone)
                        localData = Object.assign(localData, { widgetTitle: localData.widgetTitle });
                    localData = Object.assign(localData, this.callbacks(), { filterWidget: this.filterWidget });
                    this.widget = new Habiteo.DigitalModelWidget(this.$refs.cutawayHolder, localData);
                    this.isLoadingCutaway = false;
                }
            },
            updateData() {
                let cutawayData = null;
                this.urlLink = this.$props.link ? this.$props.link : this.apiUrl + '/request-data/cutaway-view' + window.location.search;
                if (this.isOfflineMode && !this.urlLink.includes('&offline'))
                    this.urlLink += '&offline';

                if (_.isEmpty(this.data) || !this.data.floors)
                    EventBus.$emit('getLoadedWidget', ['cutaway', 'plot-grid'], this.widgetId, result => {
                        cutawayData = result;
                    });
                else
                    cutawayData = this.data;
                if (cutawayData == null) {
                    this.getCutawayViewData().then(cutawayData => {
                        let floorIndex;
                        let availableFloors = [];
                        let hotspotList = [];
                        let hotspotNameList = [];
                        let currentHotspotList;

                        cutawayData = Object.assign(cutawayData, this.callbacks(), { filterWidget: this.filterWidget });

                        cutawayData.floors.forEach((floor, index) => {
                            // Get active floor
                            if (floor.active === true)
                                floorIndex = index;

                            // Get all hotspot from each floor
                            if (floor.hotspots)
                                if (hotspotList.length >= 1)
                                    hotspotList = hotspotList.concat(floor.hotspots);
                                else
                                    hotspotList = floor.hotspots;

                            // Get unique plot counter by floor
                            currentHotspotList = [];
                            floor.hotspots.forEach(hotspot => {
                                if (!hotspotNameList.includes(hotspot.name)) {
                                    currentHotspotList.push(hotspot);
                                    hotspotNameList.push(hotspot.name);
                                }
                            });

                            // Get all floor
                            availableFloors.push({
                                'id': floor.id,
                                'text': floor.text,
                                'hotspots': currentHotspotList,
                                'active': false
                            });
                        });

                        cutawayData.id = this.widgetId;
                        cutawayData.type = 'cutaway';
                        cutawayData.gui.type = 'cutaway';
                        cutawayData.widgetType = `cutaway-${this.widgetId}`;

                        cutawayData.frames = cutawayData.floors[floorIndex].frames || [];
                        cutawayData.hotspots = cutawayData.floors[floorIndex].hotspots || [];
                        cutawayData.images = this.getFloorsImages(cutawayData.floors);
                        cutawayData.startLevel = floorIndex;

                        let areaFilter = {};
                        cutawayData.areaUnit = this.getAreaUnit(hotspotList);
                        areaFilter[cutawayData.areaUnit] = this.getInterval(hotspotList, 'surface');

                        if (!cutawayData.filters)
                            cutawayData.filters = {
                                'floors': availableFloors,
                                'priceInterval': this.getInterval(hotspotList, 'price'),
                                'availabilities': this.getPlotAvailabilityList(hotspotList),
                                'tags': this.getTagList(hotspotList),
                                'typologies': this.getTypologyList(hotspotList),
                                'searchValue': null,
                                'areaInterval': this.language !== 'en' ? areaFilter : this.convertByUnit(areaFilter[cutawayData.areaUnit])
                            };

                        if (cutawayData.language)
                            this.$i18n.locale = cutawayData.language;

                        if (!this.standalone)
                            cutawayData = Object.assign(cutawayData, { widgetTitle: cutawayData.widgetTitle });

                        if (cutawayData.startIndex)
                            this.editProperties.viewAngle = parseInt(cutawayData.startIndex);

                        if (cutawayData.compass) {
                            this.useCompass = cutawayData.compass.active;
                            this.editProperties.north = parseInt(cutawayData.compass.index);
                        }

                        if (cutawayData.zoomFactor)
                            this.defaultProperties.zoomFactor = cutawayData.zoomFactor;

                        if (cutawayData.language)
                            this.$i18n.locale = cutawayData.language;

                        this.loadWidget(cutawayData);
                        this.isLoadingCutaway = false;
                    },
                    errorMsg => {
                        console.error('Error', errorMsg)
                    });
                } else {
                    let floorIndex = cutawayData.floors.findIndex(floor => floor.active);
                    cutawayData.widgetType = `cutaway-${this.widgetId}`;
                    cutawayData.frames = cutawayData.floors[floorIndex].frames || [];
                    cutawayData.hotspots = cutawayData.floors[floorIndex].hotspots || [];
                    cutawayData.images = this.getFloorsImages(cutawayData.floors);
                    cutawayData.startLevel = floorIndex;
                    EventBus.$emit('updateSidebar', cutawayData, false);
                    this.loadWidget(cutawayData);
                }
            },
            closeDialog() {
                this.userChoiceClosedSearchPopup = true;
            },
            getFloorsImages(floors){
                var images = [];
                if (floors !== null)
                    floors.forEach((floor, index) => {
                        if ('images' in floor)
                            images[index] = floor.images.length ? floor.images[0] : null;
                    });
                return images;
            },
            getAreaUnit(hotspotList) {
                let unit = null;
                if (hotspotList) {
                    hotspotList.forEach(hotspot => {
                        if (unit !== null)
                            return;

                        let area = hotspot.details.filter(detail => detail.id == 'surface');
                        if (area.length > 0) {
                            unit = area[0].unit;
                        }
                    });
                }
                return unit;
            },
            convertByUnit(areaInterval) {
                return {
                    'sq ft': areaInterval,
                    'sq m': {
                        'min': 0,
                        'max': Math.round(((areaInterval['max'] * this.squareMeterValue) + Number.EPSILON) * 100) / 100
                    }
                };
            },
            getAPIUrl() {
                let urlLink = this.$props.link ? this.$props.link : this.apiUrl + '/request-data/cutaway-view' + window.location.search;
                if (this.language && !urlLink.includes('&language'))
                    urlLink += '&language=' + this.language;
                if (this.isOfflineMode && !urlLink.includes('&offline'))
                    urlLink += '&offline';
                return urlLink;
            },
            getCutawayViewData() {
                // Start loading animation for cutaway-view-widget
                this.isLoadingCutaway = true;
                return new Promise((successCallback, failureCallback) => {
                    Http.getData(this.urlLink).then(payload => {
                        if (payload) {
                            // Rename key 'levels' to 'floors'
                            delete Object.assign(payload, {['floors']: payload['levels'] })['levels'];
                            successCallback(payload);
                        }
                    }).catch(error => {
                        EventBus.$emit('setErrorState', true, error);
                        failureCallback('can\'t retrieve data from', this.urlLink);
                    });
                });
            },
            updateWidget(item) {
                EventBus.$emit('saveSidebar');
                EventBus.$emit('updateLoadedWidgets', {
                    id: 'plot',
                    link: item.link,
                    active: true,
                    widgetType: 'plot'
                });
            },
            loadWidget(data) {
                data.type = this.cutawayOptions.includes(data.lotView) ? 'cutaway' : 'plot-grid';
                if (this.cutawayOptions.includes(data.lotView)) {
                    this.loadHabiteoWidget(data);
                }

                if (this.redirectPlotGrid && data.type === 'cutaway' && data.lotView === 'both') {
                    data.type = 'plot-grid';
                    this.redirectPlotGrid = false;
                }

                if (this.standalone) {
                    EventBus.$emit('setSidebarData', data);
                    EventBus.$emit('updateLoadedWidgets', {
                        id: data.type,
                        link: this.urlLink,
                        active: true,
                        widgetType: data.type
                    });
                    EventBus.$emit('savePayload', data);
                } else
                    EventBus.$emit('updateSidebar', data, false);

                EventBus.$emit('trackWidget', data.tracking);
            },
            loadHabiteoWidget(data) {
                if (this.widget === null)
                    this.widget = new Habiteo.DigitalModelWidget(this.$refs.cutawayHolder, data);
                else
                    this.widget.loadData(data);
            },
            filterWidget() {
                let floorIndex = this.$props.data.floors.findIndex(floor => floor.active);
                let payload = {};
                payload = Object.assign(this.$props.data, payload, this.callbacks());
                payload.frames = this.$props.data.floors[floorIndex].frames || [];
                payload.hotspots = this.$props.data.floors[floorIndex].hotspots || [];
                payload.widgetType = `cutaway-${this.widgetId}`;

                payload.startLevel = floorIndex;
                payload.startIndex = this.editProperties.viewAngle;
                payload.zoomFactor = this.editProperties.zoomFactor;
                EventBus.$emit('updateSidebar', payload, false);
                this.widget.loadData(payload);
                this.filterHotspot();
            },
            getFilters() {
                let filters = {};
                this.data.search.forEach(el => {
                    filters[el.id] = [];
                    el.items.forEach(item => {
                        if (item.active)
                            filters[el.id].push(item.id);
                    });
                });
                let str = "";
                for (let key in filters) {
                    if (str != "")
                        str += "&";
                    str += key + "=" + filters[key].join(',');
                }
                return this.data.floors.filter(el => el.active)[0].link + '&' + str;
            },
            triggerResize() {
                if (this.isIE) {
                    let evt = window.document.createEvent('UIEvents');
                    evt.initUIEvent('resize', true, false, window, 0);
                    window.dispatchEvent(evt);
                } else
                    window.dispatchEvent(new Event('resize'));
            },
            antiScroll() {
                if (this.widget) {
                    this.widget.unlockMouseWheel();
                    this.toggleModeWheel = !this.toggleModeWheel;
                }
            },
            getKeyValue(key, plot) {
                var result = plot.details.find(detail => detail.id === key);
                return result ? result['value'] : null;
            },
            getKeyType(key, plot) {
                var result = plot.details.find(detail => detail.id === key);
                return result ? result['type'] : null;
            },
            getInterval(hotspotList, type) {
                let intervals = {'min': 0, 'max': 0};
                if (hotspotList)
                    hotspotList.forEach(hotspot => {
                        if (hotspot.visible === true && hotspot.name) {
                            let tmpValue = this.getKeyValue(type, hotspot);
                            let currentValue = this.getFormatedValue(tmpValue, type);
                            intervals['max'] = intervals['max'] < currentValue ? currentValue : intervals['max'];
                        }
                    });
                return intervals;
            },
            getFormatedValue(value, type) {
                if (!value)
                    return 0;

                switch (type) {
                    case 'price':
                        return value ? parseFloat(value.match(/\d+/g).join('')) : 0;
                    case 'surface':
                        value = value.replace(/\s/g, '');
                        if (this.language === 'en')
                            value = value.replace(',', '');
                        else
                            value = value.replace('.', '');
                        return value ? parseFloat(value.match(/[0-9]+/g)) : 0;
                }
            },
            getPlotAvailabilityList(hotspotList) {
                if (hotspotList.length > 0) {
                    let availabilityList = {};
                    let currentAvailability = null;
                    let currentAvailabilityType = null;
                    let tmpAvailabilityList = {};
                    hotspotList.forEach((hotspot) => {
                        if (hotspot.name !== null) {
                            currentAvailability = this.getKeyValue('availability', hotspot);
                            currentAvailabilityType = this.getKeyType('availability', hotspot);
                            if (currentAvailability !== null) {
                                if (tmpAvailabilityList[currentAvailability]) {
                                    if (!tmpAvailabilityList[currentAvailability].hotspots.includes(hotspot.name))
                                        tmpAvailabilityList[currentAvailability].hotspots.push(hotspot.name);
                                } else
                                    tmpAvailabilityList[currentAvailability] = {
                                        color: hotspot.color,
                                        active: false,
                                        type: currentAvailabilityType,
                                        hotspots: [hotspot.name]
                                    };
                            }
                        }
                    });

                    // //Sort by availability type
                    this.availabilityTypeSortOrder.forEach((key) => {
                        var found = false;
                        Object.keys(tmpAvailabilityList).filter(availability => {
                            if (!found && tmpAvailabilityList[availability].type == key) {
                                availabilityList[availability] = tmpAvailabilityList[availability];
                                found = true;
                                return false;
                            } else
                                return true;
                        })
                    });

                    return availabilityList;
                } else
                    return {};
            },
            getTypologyList(hotspotList) {
                if (hotspotList.length > 0) {
                    let typologyList = {};
                    let currentTypology = null;
                    let tmpTypologyList = {};
                    hotspotList.forEach((hotspot) => {
                        if (hotspot.name !== null) {
                            currentTypology = this.getKeyValue('category', hotspot);
                            if (currentTypology !== null) {
                                if (tmpTypologyList[currentTypology]) {
                                    if (!tmpTypologyList[currentTypology].hotspots.includes(hotspot.name))
                                        tmpTypologyList[currentTypology].hotspots.push(hotspot.name);
                                } else
                                    tmpTypologyList[currentTypology] = {
                                        active: false,
                                        hotspots: [hotspot.name]
                                    }
                            }
                        }
                    });

                    //Sort by typology text
                    Object.keys(tmpTypologyList).sort().forEach((key) => {
                        typologyList[key] = tmpTypologyList[key];
                    });

                    return typologyList;
                } else
                    return {};
            },
            filterHotspot() {
                if (this.widget) {
                    let floorIndex = this.data.floors.findIndex(floor => floor.active);
                    let digitalWidget = this.widget;
                    EventBus.$emit('filterHotspots', this.data.filters, (filteredFloors) => {
                        digitalWidget.refreshHotspots(filteredFloors[floorIndex].hotspots);
                    });
                }
            },
            getHotspotList() {
                let hotspotsList = [];
                let currentFloor = this.data.floors.findIndex(floor => floor.active)
                if (this.data.floors)
                    hotspotsList = this.data.floors[currentFloor].hotspots.filter(el => el.visible);
                return hotspotsList;
            },
            getTagList(hotspotList) {
                let tagList = [], addedTagList = [];
                hotspotList.forEach(hotspot => {
                    let tags = hotspot.details && hotspot.details.filter(el => el.id === "tags" && el.tags !== null);
                    if (tags[0]) {
                        Object.entries(tags[0].tags).forEach(tag => {
                            if (!addedTagList.includes(tag[0])) {
                                tagList.push({
                                    id: tag[0],
                                    color: tag[1].color,
                                    type: tag[1].type,
                                    active: false
                                })
                                addedTagList.push(tag[0]);
                            }
                        });
                    }
                });

                if (tagList.length > 0)
                    tagList.sort((a, b) => (a.type > b.type) ? 1 : (a.type === b.type) ? ((a.id > b.id) ? 1 : -1) : -1 );

                return tagList;
            },
            updateScale() {
                if (this.widget)
                    this.widget.updateScale();
            }
        },
        watch: {
            active(val) {
                if (val) {
                    this.updateData();
                    this.triggerResize();
                }
            },
            'data.zoomFactor'(val) {
                if (val)
                    this.defaultProperties.zoomFactor = this.data.zoomFactor;
            },
            floorIndex() {
                this.userChoiceClosedSearchPopup = false;
            }
        }
    };
</script>

<style scoped>
    .editkrpano{
        position: absolute;
        top:75px;
    }
</style>


// WEBPACK FOOTER //
// src/views/CutawayWidget.vue